import { computed, onScopeDispose, reactive, ref, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import CaptainAssistantAPI from 'dashboard/api/captain/assistant';
import { ACTION_TYPES } from 'dashboard/helper/captain/agentActionTypes';
import { useToolApprovals } from './useToolApprovals';
const APPROVAL_POLICY_LOCKED_TYPES = new Set([
  ACTION_TYPES.WRITE,
  ACTION_TYPES.EXTERNAL,
  ACTION_TYPES.DANGEROUS,
]);
export const useToolManagement = ({
  store,
  t,
  assistantId,
  hasAssistantContext,
  canManageAssistant,
  hasAdvancedOperations,
  hasAdvancedApprovals,
  api = CaptainAssistantAPI,
  notify = useAlert,
}) => {
  const isFetchingManagement = ref(false);
  const isSavingGovernance = ref(false);
  const savingActionId = ref(null);
  const savingAvailabilityId = ref(null);
  const actionSearch = ref('');
  const actions = ref([]);
  const availabilityActionPending = ref(null);
  const governanceForm = reactive({
    featureFaq: false,
    featureMemory: false,
    allowSensitiveData: false,
    disableDatabaseTools: false,
  });
  let active = true;
  let managementRequestId = 0;
  const scenarios = computed(
    () => store.getters['captainScenarios/getRecords'] || []
  );
  const assistant = computed(
    () =>
      store.getters['captainAssistants/getRecord']?.(assistantId.value) || {}
  );
  const currentConfig = computed(() => assistant.value.config || {});
  const filteredActions = computed(() => {
    const query = actionSearch.value.trim().toLowerCase();
    if (!query) return actions.value;
    return actions.value.filter(action =>
      [action.name, action.description, action.permission, action.risk_level]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query)
    );
  });
  const actionTypeLabels = computed(() => ({
    [ACTION_TYPES.READ]: t('CAPTAIN.CUSTOM_TOOLS.ACTION_TYPES.READ'),
    [ACTION_TYPES.WRITE]: t('CAPTAIN.CUSTOM_TOOLS.ACTION_TYPES.WRITE'),
    [ACTION_TYPES.HANDOFF]: t('CAPTAIN.CUSTOM_TOOLS.ACTION_TYPES.HANDOFF'),
    [ACTION_TYPES.EXTERNAL]: t('CAPTAIN.CUSTOM_TOOLS.ACTION_TYPES.EXTERNAL'),
    [ACTION_TYPES.DANGEROUS]: t('CAPTAIN.CUSTOM_TOOLS.ACTION_TYPES.DANGEROUS'),
  }));
  const isCurrentAssistant = targetAssistantId =>
    active && targetAssistantId === assistantId.value;
  const {
    approvalActionId,
    pendingApprovals,
    approvalsMeta,
    isLoadingMoreApprovals,
    hasMoreApprovals,
    fetchApprovals,
    loadMoreApprovals,
    decideApproval,
    resetApprovals,
  } = useToolApprovals({
    t,
    api,
    notify,
    assistantId,
    canManageAssistant,
    hasAdvancedApprovals,
    isCurrentAssistant,
  });
  const linkedScenarioCount = action =>
    scenarios.value.filter(scenario =>
      (scenario.tools || []).map(String).includes(String(action.id))
    ).length;
  const linkedCustomScenarioCount = tool =>
    linkedScenarioCount({ id: tool?.slug });
  const isActionApprovalLocked = action =>
    APPROVAL_POLICY_LOCKED_TYPES.has(action.risk_level);
  const syncGovernanceForm = () => {
    governanceForm.featureFaq = currentConfig.value.feature_faq ?? false;
    governanceForm.featureMemory = currentConfig.value.feature_memory ?? false;
    governanceForm.allowSensitiveData = Boolean(
      currentConfig.value.allow_sensitive_data
    );
    governanceForm.disableDatabaseTools = Boolean(
      currentConfig.value.disable_database_tools
    );
  };
  const updateGovernance = value => Object.assign(governanceForm, value);
  const fetchActions = async (targetAssistantId = assistantId.value) => {
    const { data } = await api.actions({ assistantId: targetAssistantId });
    return data.payload || [];
  };
  const fetchToolManagement = async () => {
    const targetAssistantId = assistantId.value;
    if (!targetAssistantId) return;
    managementRequestId += 1;
    const currentRequestId = managementRequestId;
    isFetchingManagement.value = true;
    try {
      const [, , actionRecords] = await Promise.all([
        store.dispatch('captainAssistants/show', targetAssistantId),
        store.dispatch('captainScenarios/get', {
          assistantId: targetAssistantId,
        }),
        fetchActions(targetAssistantId),
        fetchApprovals({ targetAssistantId }),
      ]);
      if (
        currentRequestId !== managementRequestId ||
        !isCurrentAssistant(targetAssistantId)
      ) {
        if (active && assistantId.value) {
          await store.dispatch('captainScenarios/get', {
            assistantId: assistantId.value,
          });
        }
        return;
      }
      actions.value = actionRecords;
      syncGovernanceForm();
    } catch (error) {
      if (
        currentRequestId === managementRequestId &&
        isCurrentAssistant(targetAssistantId)
      ) {
        notify(
          error?.message || t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.LOAD_ERROR')
        );
      }
    } finally {
      if (currentRequestId === managementRequestId) {
        isFetchingManagement.value = false;
      }
    }
  };
  const refreshAfterCustomToolChange = async targetAssistantId => {
    if (!targetAssistantId) return;
    const [records] = await Promise.all([
      fetchActions(targetAssistantId),
      store.dispatch('captainScenarios/get', {
        assistantId: targetAssistantId,
      }),
    ]);
    if (isCurrentAssistant(targetAssistantId)) actions.value = records;
  };

  const toggleActionAvailability = async action => {
    if (!canManageAssistant.value) return;
    const targetAssistantId = assistantId.value;
    const disabledActions = new Set(
      (currentConfig.value.disabled_tool_ids || []).map(String)
    );
    const actionId = String(action.id);
    if (action.enabled) {
      disabledActions.add(actionId);
    } else {
      disabledActions.delete(actionId);
    }
    savingAvailabilityId.value = action.id;
    try {
      await store.dispatch('captainAssistants/update', {
        id: targetAssistantId,
        config: {
          ...currentConfig.value,
          disabled_tool_ids: Array.from(disabledActions),
        },
      });
      const records = await fetchActions(targetAssistantId);
      if (!isCurrentAssistant(targetAssistantId)) return;
      actions.value = records;
      notify(t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.AVAILABILITY_UPDATED'));
    } catch (error) {
      if (isCurrentAssistant(targetAssistantId)) {
        notify(
          error?.message ||
            t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.AVAILABILITY_UPDATE_ERROR')
        );
      }
    } finally {
      if (savingAvailabilityId.value === action.id) {
        savingAvailabilityId.value = null;
      }
    }
  };

  const requestToggleActionAvailability = action => {
    if (!canManageAssistant.value) return undefined;
    if (action.enabled && linkedScenarioCount(action) > 0) {
      availabilityActionPending.value = action;
      return undefined;
    }
    toggleActionAvailability(action);
    return undefined;
  };
  const confirmToggleActionAvailability = async () => {
    const action = availabilityActionPending.value;
    availabilityActionPending.value = null;
    if (action) await toggleActionAvailability(action);
  };
  const closeActionAvailabilityDialog = () => {
    availabilityActionPending.value = null;
  };

  const toggleActionApproval = async action => {
    if (
      !canManageAssistant.value ||
      !hasAdvancedApprovals.value ||
      isActionApprovalLocked(action)
    ) {
      return;
    }
    const targetAssistantId = assistantId.value;
    const configuredActions = new Set(
      (currentConfig.value.approval_required_tool_ids || []).map(String)
    );
    const actionId = String(action.id);
    if (configuredActions.has(actionId)) {
      configuredActions.delete(actionId);
    } else {
      configuredActions.add(actionId);
    }
    savingActionId.value = action.id;
    try {
      await store.dispatch('captainAssistants/update', {
        id: targetAssistantId,
        config: {
          ...currentConfig.value,
          approval_required_tool_ids: Array.from(configuredActions),
        },
      });
      const records = await fetchActions(targetAssistantId);
      if (!isCurrentAssistant(targetAssistantId)) return;
      actions.value = records;
      notify(t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.APPROVAL_UPDATED'));
    } catch (error) {
      if (isCurrentAssistant(targetAssistantId)) {
        notify(
          error?.message ||
            t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.APPROVAL_UPDATE_ERROR')
        );
      }
    } finally {
      if (savingActionId.value === action.id) savingActionId.value = null;
    }
  };

  const saveGovernance = async () => {
    if (!canManageAssistant.value) return;
    const targetAssistantId = assistantId.value;
    isSavingGovernance.value = true;
    try {
      await store.dispatch('captainAssistants/update', {
        id: targetAssistantId,
        config: {
          ...currentConfig.value,
          feature_faq: governanceForm.featureFaq,
          feature_memory: governanceForm.featureMemory,
          allow_sensitive_data: governanceForm.allowSensitiveData,
          disable_database_tools: governanceForm.disableDatabaseTools,
        },
      });
      const records = await fetchActions(targetAssistantId);
      if (!isCurrentAssistant(targetAssistantId)) return;
      actions.value = records;
      notify(t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.GOVERNANCE_UPDATED'));
    } catch (error) {
      if (isCurrentAssistant(targetAssistantId)) {
        notify(
          error?.message ||
            t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.GOVERNANCE_UPDATE_ERROR')
        );
      }
    } finally {
      isSavingGovernance.value = false;
    }
  };

  const resetAssistantState = () => {
    managementRequestId += 1;
    isFetchingManagement.value = false;
    isSavingGovernance.value = false;
    savingActionId.value = null;
    savingAvailabilityId.value = null;
    availabilityActionPending.value = null;
    actions.value = [];
    resetApprovals();
  };

  const stopAssistantWatch = watch(
    assistantId,
    () => {
      resetAssistantState();
      if (hasAdvancedOperations.value && hasAssistantContext.value) {
        fetchToolManagement();
      }
    },
    { immediate: true }
  );

  onScopeDispose(() => {
    active = false;
    resetAssistantState();
    stopAssistantWatch();
  });
  return {
    isFetchingManagement,
    isSavingGovernance,
    savingActionId,
    savingAvailabilityId,
    approvalActionId,
    actionSearch,
    actions,
    filteredActions,
    actionTypeLabels,
    pendingApprovals,
    approvalsMeta,
    isLoadingMoreApprovals,
    hasMoreApprovals,
    availabilityActionPending,
    governanceForm,
    linkedScenarioCount,
    linkedCustomScenarioCount,
    isActionApprovalLocked,
    updateGovernance,
    fetchToolManagement,
    refreshAfterCustomToolChange,
    loadMoreApprovals,
    requestToggleActionAvailability,
    confirmToggleActionAvailability,
    closeActionAvailabilityDialog,
    toggleActionApproval,
    saveGovernance,
    decideApproval,
  };
};
